import React, { useState, useEffect } from 'react';
import { StatusBar } from 'expo-status-bar';
import { Platform, View, StyleSheet, ImageBackground } from 'react-native';
import NavigationBar from './components/NavigationBar';
import LoadingScreen from './components/LoadingScreen';
import DailyDecisionPopup from './components/DailyDecisionPopup';
import PersistentWidgetPopup from './components/PersistentWidgetPopup';
import CharacterPopupContainer from './components/CharacterPopup';
import { useNavigation } from './context/NavigationContext';
import { useAuth } from './context/AuthContext';
import { useDailyDecision } from './context/DailyDecisionContext';
import { usePersistentWidget } from './context/PersistentWidgetContext';
import ContextContainer from './context/ContextContainer';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import * as Font from 'expo-font';

// Import all screens
import LandingScreen from './screens/LandingScreen';
import LoginScreen from './screens/LoginScreen';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen';
import MyDashboardScreen from './screens/MyDashboardScreen';
import GettingStartedScreen from './screens/GettingStartedScreen';
import CoursesScreen from './screens/CoursesScreen';
import GlossaryScreen from './screens/GlossaryScreen';
import CalendarScreen from './screens/CalendarScreen';
import ProfileScreen from './screens/ProfileScreen';
import AdminScreen from './screens/AdminScreen';
import ComingSoonScreen from './screens/ComingSoonScreen';
import MaintenanceScreen from './screens/MaintenanceScreen';
import SuspendedScreen from './screens/SuspendedScreen';
import TigerTeamScreen from './screens/TigerTeamScreen';

// Screens that should hide the navbar
const FULLSCREEN_SCREENS = ['ComingSoon', 'Maintenance', 'Landing'];

// Screen Router Component
function ScreenRouter() {
  const { currentScreen } = useNavigation();

  // Render screens based on currentScreen
  switch (currentScreen) {
    case 'Landing':
      return <LandingScreen />;
    case 'Login':
      return <LoginScreen />;
    case 'ForgotPassword':
      return <ForgotPasswordScreen />;
    case 'MyDashboard':
      return <MyDashboardScreen />;
    case 'GettingStarted':
      return <GettingStartedScreen />;
    case 'Courses':
      return <CoursesScreen />;
    case 'Glossary':
      return <GlossaryScreen />;
    case 'Calendar':
      return <CalendarScreen />;
    case 'Profile':
      return <ProfileScreen />;
    case 'Settings':
      return <ProfileScreen />;
    case 'Admin':
      return <AdminScreen />;
    case 'ComingSoon':
      return <ComingSoonScreen />;
    case 'Maintenance':
      return <MaintenanceScreen />;
    case 'Suspended':
      return <SuspendedScreen />;
    case 'TigerTeam':
      return <TigerTeamScreen />;
    default:
      return <LandingScreen />;
  }
}

function AppContent() {
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const { mode } = useTheme();
  const { loading: authLoading } = useAuth();
  const { initializing, Banner, currentScreen } = useNavigation();
  const { 
    showDailyDecision, 
    todaysQuestion, 
    reviewMode,
    todaysAnswer,
    loading: dailyDecisionLoading 
  } = useDailyDecision();
  const { 
    openWidgetId,
    availableWidgets,
    loading: persistentWidgetLoading 
  } = usePersistentWidget();

  // Debug logging


  useEffect(() => {
    loadFonts();
  }, []);

  const loadFonts = async () => {
    await Font.loadAsync({
      'Chivo': require('./assets/fonts/Chivo-VariableFont_wght.ttf'),
      'Archivo': require('./assets/fonts/Archivo-VariableFont_wdth,wght.ttf'),
      'Plus Jakarta Sans': require('./assets/fonts/PlusJakartaSans-VariableFont_wght.ttf'),
      'Sen': require('./assets/fonts/Sen-VariableFont_wght.ttf'),
      'Public Sans': require('./assets/fonts/PublicSans-VariableFont_wght.ttf'),
    });
    setFontsLoaded(true);
  };

  if (!fontsLoaded) {
    return <StatusBar style="auto" />;
  }

  // Check if we should hide the navbar
  const shouldHideNavbar = FULLSCREEN_SCREENS.includes(currentScreen);

  const backgroundContent = (
    <>
      {Banner && <Banner />}

      {(initializing || authLoading) && <LoadingScreen />}

      {!initializing && !authLoading && (
        <>
          {!shouldHideNavbar && <NavigationBar />}
          <View style={styles.contentWrapper}>
            <StatusBar style="auto" />
            <ScreenRouter />
          </View>
          
          {/* Daily Decision Popup */}
          {showDailyDecision && !dailyDecisionLoading && (reviewMode ? todaysAnswer : todaysQuestion) && (
            <DailyDecisionPopup />
          )}
          
          {/* Persistent Widget Popups - One for each open widget */}
          {!persistentWidgetLoading && availableWidgets.map(widget => (
            <PersistentWidgetPopup key={widget.id} widgetId={widget.id} />
          ))}
          
          {/* Character Popups for gamification */}
          <CharacterPopupContainer />
        </>
      )}
    </>
  );

  // Load background image for all platforms  
  const backgroundImage = require('./assets/background.png');

  // Use ImageBackground for all platforms - React Native Web handles this properly
  return (
    <View 
      style={styles.scrollWrapper}
      nativeID="scroll-container"
    >
      <ImageBackground
        source={backgroundImage}
        style={[
          styles.scrollContainer, 
          { 
            backgroundColor: mode === 'dark' ? '#080808' : '#F7F7F8',
            ...Platform.select({
              web: {
                minHeight: '100vh',
                width: '100%',
              }
            })
          }
        ]}
        resizeMode="cover"
        imageStyle={styles.backgroundImage}
      >
        {/* Blur overlay - matches mobile app's BlurView */}
        <View style={[
          styles.blurOverlay,
          {
            backgroundColor: mode === 'dark' ? 'rgba(8,8,8,0.3)' : 'rgba(241,241,241,0.3)',
          }
        ]} />
        <View style={[
          styles.overlay, 
          { 
            ...Platform.select({
              web: { minHeight: '100vh' }
            })
          }
        ]}>
          {backgroundContent}
        </View>
      </ImageBackground>
    </View>
  );
}

export default function App() {
  return (
    <ContextContainer>
      <AppContent />
    </ContextContainer>
  );
}

const styles = StyleSheet.create({
  scrollWrapper: {
    flex: 1,
    ...Platform.select({
      web: {
        width: '100vw',
        height: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
      },
    }),
  },
  scrollContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    padding: 0,
    margin: 0,
    position: 'relative',
    minHeight: '100%',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    padding: 0,
    margin: 0,
    width: '100%',
  },
  backgroundImage: {
    ...Platform.select({
      web: {
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
      }
    })
  },
  blurOverlay: {
    ...Platform.select({
      web: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        backdropFilter: 'blur(15px)',
        WebkitBackdropFilter: 'blur(15px)', // Safari support
        zIndex: 0,
        pointerEvents: 'none',
      }
    })
  },
  overlay: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    padding: 0,
    margin: 0,
    position: 'relative',
    zIndex: 1,
  },
});
